/** @format */

import ArrowIconLeft from '@/assets/joivy_arrow_left.svg';
import ArrowIconRight from '@/assets/joivy_arrow_right.svg';
import CloseButtonIcon from '@/assets/joivy_close_miniature.svg';
import { GTMPDPRoomGalleryCarouselClick } from '@/ducks/googleTagManager/helpers/google-tag-manager';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import { Container } from '../../index.style';

type Photos = (
  | {
      index: number;
      url: string;
      type: string;
      poster: any;
      alt?: undefined;
      thumbnail?: undefined;
      representative?: undefined;
    }
  | {
      index: number;
      url: string;
      alt: string;
      thumbnail: string;
      representative: boolean;
      type?: undefined;
      poster?: undefined;
    }
  | undefined
)[];

interface LightboxProps {
  gtmInfo: {
    roomCode: string;
    monthlyRate: number;
  };
  photos: Photos;
  selectedImageIndex: number | null;
  setSelectedImageIndex: React.Dispatch<React.SetStateAction<number | null>>;
}

const Lightbox: React.FC<LightboxProps> = ({ photos, gtmInfo, selectedImageIndex, setSelectedImageIndex }) => {
  const LightBoxSwiperCarouselRef = useRef<SwiperRef>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(selectedImageIndex ?? 0);

  const closeLightbox = () => {
    setSelectedImageIndex(null);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        if (LightBoxSwiperCarouselRef.current) LightBoxSwiperCarouselRef.current.swiper.slidePrev();
      } else if (event.key === 'ArrowRight') {
        if (LightBoxSwiperCarouselRef.current) LightBoxSwiperCarouselRef.current.swiper.slideNext();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, photos]);

  useEffect(() => {
    if (selectedImageIndex !== null) setIsOpen(true);
  }, [selectedImageIndex]);

  const handleSlideChange = () => {
    if (LightBoxSwiperCarouselRef.current) {
      GTMPDPRoomGalleryCarouselClick(
        gtmInfo?.roomCode,
        gtmInfo.monthlyRate,
        LightBoxSwiperCarouselRef.current.swiper.activeIndex + 1,
      );
      setActiveIndex(LightBoxSwiperCarouselRef.current.swiper.activeIndex);
    }
  };

  return (
    <>
      {isOpen && selectedImageIndex != null && photos && (
        <Overlay>
          <Container>
            <PrevButton className="swiper-button-prev">
              <img src={ArrowIconLeft} alt="Previous" width={42} />
            </PrevButton>
            <NextButton className="swiper-button-next">
              <img src={ArrowIconRight} alt="Next" width={42} />
            </NextButton>
            <Modal>
              <InfoContainer>
                <div />
                <Counter>{`${activeIndex + 1}/${photos.length}`}</Counter>
                <CloseButton onClick={closeLightbox}>
                  <img src={CloseButtonIcon} alt="Close" width={20} />
                </CloseButton>
              </InfoContainer>
              <Swiper
                loop={false}
                key={photos.length + '-lightbox-swiper'}
                ref={LightBoxSwiperCarouselRef}
                navigation={{
                  prevEl: '.swiper-button-prev',
                  nextEl: '.swiper-button-next',
                }}
                slideToClickedSlide={true}
                slidesPerView={1}
                onSlideChange={handleSlideChange}
                initialSlide={activeIndex}
                runCallbacksOnInit={false}
              >
                {photos &&
                  photos.map((photo, index) =>
                    photo ? (
                      <SwiperSlide key={index}>
                        <ImgLightBoxContainer>
                          <img src={photo.url} alt={`Selected Image ${index + 1}`} />
                        </ImgLightBoxContainer>
                      </SwiperSlide>
                    ) : null,
                  )}
              </Swiper>
            </Modal>
          </Container>
        </Overlay>
      )}
    </>
  );
};

const ImgLightBoxContainer = styled.div`
  width: 100%;
  height: 100%;
  max-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 40px 0 !important;
`;

const Counter = styled.div`
  color: ${({ theme }) => theme.colors.joivy.white};
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.joivy.black};
  opacity: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 112;

  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    align-items: flex-start;
  }
`;

const Modal = styled.div`
  position: relative;
  max-width: 878px;
  margin: 0 auto;
  img {
    max-width: 100%;
    height: auto;
  }
`;

const CloseButton = styled.button`
  padding: 5px;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.joivy.white};
  border: 0;
  border-radius: 5px;
  cursor: pointer;

  img {
    filter: brightness(0) invert(1);
  }
`;

const PrevButton = styled.button`
  position: absolute;
  top: 55%;
  left: 10px;
  transform: translateY(-50%);
  padding: 10px;
  background-color: transparent;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.joivy.white};
  border: 0;
  border-radius: 5px;
  cursor: pointer;
  font-size: 24px;
  &:after {
    content: none !important;
  }
  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    display: none !important;
  }
`;

const NextButton = styled.button`
  position: absolute;
  top: 55%;
  right: 10px;
  transform: translateY(-50%);
  padding: 10px;
  background-color: transparent;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.joivy.white};
  border: 0;
  border-radius: 5px;
  cursor: pointer;
  font-size: 24px;
  &:after {
    content: none !important;
  }
  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    display: none !important;
  }
`;

export default Lightbox;
